import { useRef, useState } from "react";
import { mergeRefs } from "react-merge-refs";
import { DismissIcon } from "../../materials/icons/dismiss-icon/DismissIcon";
import { SearchIcon } from "../../materials/icons/search-icon/SearchIcon";
import {
  ClearButton,
  ClearButtonWrapper,
  Input,
  InputWrapper,
  PlaceholderOverlay,
  Props,
  SearchButton,
  Wrapper,
} from "./Search.Parts";

export const Search = ({
  defaultActive,
  placeholder,
  type,
  width,
  ref,
  ...inputProps
}: Props) => {
  const inputWrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [active, setActive] = useState(Boolean(defaultActive));
  const [hasValue, setHasValue] = useState(Boolean(inputProps.defaultValue));

  const handleClear = () => {
    if (!inputRef.current) return;

    // Get the native value setter from the input element's prototype
    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
      HTMLInputElement.prototype,
      "value",
    )?.set;

    if (!nativeInputValueSetter) return;

    // Use the native setter to update the value in a way React recognizes
    nativeInputValueSetter.call(inputRef.current, "");

    // Dispatch an input event to notify React's synthetic event system
    const event = new Event("input", { bubbles: true });
    inputRef.current.dispatchEvent(event);

    setHasValue(false);

    // Focus the input after clearing
    inputRef.current?.focus();
  };

  const handleToggle = () => {
    setActive((prev) => {
      // If the input is already active and has a value, do nothing
      if (prev && inputRef.current?.value) return prev;

      // If the input is not active and has no value, focus it after the transition
      if (!prev && !inputRef.current?.value) {
        inputWrapperRef.current?.addEventListener(
          "transitionend",
          () => inputRef.current?.focus(),
          { once: true },
        );
      }
      return !prev;
    });
  };

  return (
    <Wrapper $active={active} $type={type}>
      <SearchButton
        onClick={handleToggle}
        type="button"
        width={width}
        tabIndex={type === "fullWidth" || hasValue ? -1 : 0}
      >
        <SearchIcon />
      </SearchButton>
      <InputWrapper ref={inputWrapperRef}>
        <Input
          {...inputProps}
          type="text"
          ref={mergeRefs([inputRef, ref])}
          // The space triggers :placeholder-shown which triggers the real placeholder in the overlay
          placeholder={placeholder ? " " : ""}
          onChange={(e) => {
            setHasValue(Boolean(e.target.value));
            inputProps.onChange?.(e);
          }}
          tabIndex={active || type === "fullWidth" ? 0 : -1}
        />
        {placeholder && <PlaceholderOverlay>{placeholder}</PlaceholderOverlay>}
        <ClearButtonWrapper>
          <ClearButton onClick={handleClear}>
            <DismissIcon small={true} />
          </ClearButton>
        </ClearButtonWrapper>
      </InputWrapper>
    </Wrapper>
  );
};
